import type { RuntimeWorldTilePayloadV2 } from "./schema/runtimeTile.ts";
import {
  pedestrianRoutePolylinesForStreetFeature,
  streetSurfaceFamilyForTile,
} from "./streets.ts";

export interface PedestrianGraphNode {
  readonly id: string;
  readonly x: number;
  readonly z: number;
  readonly neighbours: readonly string[];
}

export interface PedestrianGraph {
  readonly nodes: readonly PedestrianGraphNode[];
  readonly nodeById: Readonly<Record<string, PedestrianGraphNode>>;
}

export interface PedestrianGraphPoint {
  readonly x: number;
  readonly z: number;
  readonly distanceM: number;
}

export interface PedestrianGraphSegmentPoint extends PedestrianGraphPoint {
  readonly from: PedestrianGraphNode;
  readonly to: PedestrianGraphNode;
  readonly alpha: number;
  readonly lengthM: number;
}

function pointId(x: number, z: number): string {
  return `${x.toFixed(1)}:${z.toFixed(1)}`;
}

/**
 * Source-derived pedestrian graph aligned with the rendered street surfaces.
 *
 * Dedicated paths retain their source centreline. Road features contribute
 * the centre of both procedural sidewalks, using the same source width,
 * surface family, and cross-section rules as the renderer.
 */
export function buildPedestrianGraph(
  tiles: readonly Readonly<RuntimeWorldTilePayloadV2>[],
): PedestrianGraph {
  const points = new Map<string, { x: number; z: number; links: Set<string> }>();
  const ensure = (x: number, z: number) => {
    const id = pointId(x, z);
    const existing = points.get(id);
    if (existing !== undefined) return { id, point: existing };
    const point = { x, z, links: new Set<string>() };
    points.set(id, point);
    return { id, point };
  };
  for (const tile of [...tiles].sort((a, b) => a.id.localeCompare(b.id))) {
    const family = streetSurfaceFamilyForTile(tile.origin, tile.sizeM);
    for (const feature of tile.transportLines) {
      const routes = pedestrianRoutePolylinesForStreetFeature(feature, family);
      for (const route of routes) {
        for (let index = 1; index < route.points.length; index += 1) {
          const from = route.points[index - 1]!;
          const to = route.points[index]!;
          const a = ensure(tile.origin.x + from.x, tile.origin.z + from.z);
          const b = ensure(tile.origin.x + to.x, tile.origin.z + to.z);
          if (a.id !== b.id) {
            a.point.links.add(b.id);
            b.point.links.add(a.id);
          }
        }
      }
    }
  }
  const nodes = [...points.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([id, point]) => ({
      id,
      x: point.x,
      z: point.z,
      neighbours: [...point.links].sort(),
    }));
  return {
    nodes,
    nodeById: Object.fromEntries(nodes.map((node) => [node.id, node])),
  };
}

export function nearbyPedestrianNodes(
  graph: Readonly<PedestrianGraph>,
  focus: Readonly<{ x: number; z: number }>,
  radiusM: number,
  limit: number,
): readonly PedestrianGraphNode[] {
  return graph.nodes
    .map((node) => ({
      node,
      distanceM: Math.hypot(node.x - focus.x, node.z - focus.z),
    }))
    .filter((entry) => entry.distanceM <= radiusM)
    .sort((a, b) =>
      a.distanceM - b.distanceM || a.node.id.localeCompare(b.node.id)
    )
    .slice(0, limit)
    .map((entry) => entry.node);
}

export function nearestPointOnPedestrianGraph(
  graph: Readonly<PedestrianGraph>,
  focus: Readonly<{ x: number; z: number }>,
  maximumDistanceM: number,
): PedestrianGraphPoint | null {
  const nearest = nearestSegmentPointOnPedestrianGraph(
    graph,
    focus,
    maximumDistanceM,
  );
  return nearest === null
    ? null
    : { x: nearest.x, z: nearest.z, distanceM: nearest.distanceM };
}

export function nearestSegmentPointOnPedestrianGraph(
  graph: Readonly<PedestrianGraph>,
  focus: Readonly<{ x: number; z: number }>,
  maximumDistanceM: number,
): PedestrianGraphSegmentPoint | null {
  let nearest:
    | (PedestrianGraphSegmentPoint & { readonly edgeId: string })
    | null = null;
  for (const from of graph.nodes) {
    for (const neighbourId of from.neighbours) {
      if (from.id >= neighbourId) {
        continue;
      }
      const to = graph.nodeById[neighbourId];
      if (to === undefined) {
        continue;
      }
      const deltaX = to.x - from.x;
      const deltaZ = to.z - from.z;
      const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
      if (lengthSquared <= 1e-6) {
        continue;
      }
      const alpha = Math.max(
        0,
        Math.min(
          1,
          (
            (focus.x - from.x) * deltaX +
            (focus.z - from.z) * deltaZ
          ) / lengthSquared,
        ),
      );
      const x = from.x + deltaX * alpha;
      const z = from.z + deltaZ * alpha;
      const distanceM = Math.hypot(x - focus.x, z - focus.z);
      const edgeId = `${from.id}>${neighbourId}`;
      if (
        distanceM <= maximumDistanceM &&
        (
          nearest === null ||
          distanceM < nearest.distanceM - 1e-6 ||
          (
            Math.abs(distanceM - nearest.distanceM) <= 1e-6 &&
            edgeId.localeCompare(nearest.edgeId) < 0
          )
        )
      ) {
        nearest = {
          x,
          z,
          distanceM,
          edgeId,
          from,
          to,
          alpha,
          lengthM: Math.sqrt(lengthSquared),
        };
      }
    }
  }
  return nearest;
}

/**
 * Keeps the current ambient cast stable while the player moves. New nodes
 * enter through the spawn radius; existing nodes leave only after crossing
 * the wider despawn radius.
 */
export function stableNearbyPedestrianNodes(
  graph: Readonly<PedestrianGraph>,
  focus: Readonly<{ x: number; z: number }>,
  spawnRadiusM: number,
  despawnRadiusM: number,
  limit: number,
  retainedNodeIds: readonly string[],
): readonly PedestrianGraphNode[] {
  if (despawnRadiusM < spawnRadiusM) {
    throw new RangeError("Pedestrian despawn radius must cover spawn radius");
  }
  const selected: PedestrianGraphNode[] = [];
  const selectedIds = new Set<string>();
  for (const id of retainedNodeIds) {
    const node = graph.nodeById[id];
    if (
      node !== undefined &&
      Math.hypot(node.x - focus.x, node.z - focus.z) <= despawnRadiusM
    ) {
      selected.push(node);
      selectedIds.add(id);
      if (selected.length === limit) {
        return selected;
      }
    }
  }
  const candidates = nearbyPedestrianNodes(
    graph,
    focus,
    spawnRadiusM,
    graph.nodes.length,
  );
  for (const node of candidates) {
    if (selectedIds.has(node.id)) {
      continue;
    }
    selected.push(node);
    selectedIds.add(node.id);
    if (selected.length === limit) {
      break;
    }
  }
  return selected;
}
